import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ChangePassword() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    if (next === current) return toast.error("New password must be different from your current password");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setDone(true);
      // Backend invalidates ALL sessions (incl. this one) — sign out locally and return to login.
      try { await logout(); } catch { /* session already gone */ }
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="change-password-card">
        <div className="flex justify-center mb-6"><Logo /></div>
        {done ? (
          <div className="text-center space-y-3" data-testid="change-password-done">
            <CheckCircle2 className="mx-auto text-brand" size={44} />
            <h1 className="text-xl font-display font-bold text-slate-800">Password changed</h1>
            <p className="text-sm text-slate-500">For your security you've been signed out everywhere. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-center">
              <h1 className="text-xl font-display font-bold text-slate-800">Change your password</h1>
              <p className="text-sm text-slate-500 mt-1">Enter your current password, then choose a new one.</p>
            </div>
            <div>
              <Label>Current password</Label>
              <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                data-testid="change-password-current" className="mt-1.5" autoComplete="current-password" />
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                data-testid="change-password-new" className="mt-1.5" placeholder="At least 8 characters" autoComplete="new-password" />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="change-password-confirm" className="mt-1.5" autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={busy} data-testid="change-password-submit" className="w-full bg-brand hover:bg-brand-hover">
              {busy ? <Loader2 className="animate-spin" size={16} /> : "Update password"}
            </Button>
            <button type="button" onClick={() => navigate(-1)} data-testid="change-password-back"
              className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
              <ArrowLeft size={15} /> Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
